/* eslint-disable no-console */
import { useEffect, useMemo, useRef } from 'react';
import { getAuthToken } from '../../../auth/tokenvalidation';
import { expectIsPertaminaFromView, isUnreadForRole } from './helpers';
import {
  clearClarificationRespondCache,
  fetchClarificationResponds,
} from './fetchClarificationResponds';
import {
  LOCK_RESOURCE_CLAUSE,
  LOCK_RESOURCE_TYPE_RESPONSE,
  markRespondReadStatus,
} from './markRespondReadStatus';

const useMarkClarificationRespondsAsRead = ({
  procurementId = null,
  procurementMitraId = null,
  clauseDetailIds = [],
  isEvaluatorView = true,
  enabled = true,
  onComplete = null,
  waitForClauseIds = false,
} = {}) => {
  const ranKeyRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const expectIsPertamina = expectIsPertaminaFromView(isEvaluatorView);

  const clauseDetailIdsKey = useMemo(
    () =>
      [...new Set((clauseDetailIds ?? []).filter(Boolean))]
        .map(String)
        .sort()
        .join(','),
    [clauseDetailIds]
  );

  const clauseIdsReady = !waitForClauseIds || Boolean(clauseDetailIdsKey);

  const runKey = useMemo(() => {
    if (!enabled || !procurementId || !procurementMitraId || !clauseIdsReady) {
      return null;
    }
    return [
      String(procurementId),
      String(procurementMitraId),
      clauseDetailIdsKey,
      String(expectIsPertamina),
    ].join('|');
  }, [
    clauseDetailIdsKey,
    clauseIdsReady,
    enabled,
    expectIsPertamina,
    procurementId,
    procurementMitraId,
  ]);

  useEffect(() => {
    if (!runKey || ranKeyRef.current === runKey) return;
    ranKeyRef.current = runKey;

    let cancelled = false;

    const markUnread = async () => {
      const token = getAuthToken();
      if (!token || cancelled) return;

      const clauseIds = clauseDetailIdsKey
        ? clauseDetailIdsKey.split(',').filter(Boolean)
        : [];

      const items = await fetchClarificationResponds({
        token,
        procurementId,
        procurementMitraId,
        clauseDetailIds: clauseIds,
        includeClause: true,
        includeTypeResponse: true,
        allowUnscoped: true,
      });

      if (cancelled) return;

      const unreadItems = items.filter(item =>
        isUnreadForRole(item, expectIsPertamina)
      );

      if (!unreadItems.length) {
        if (!cancelled && typeof onCompleteRef.current === 'function') {
          onCompleteRef.current();
        }
        return;
      }

      const results = await Promise.allSettled(
        unreadItems.map(item => {
          const resource =
            item?.typeResponse != null
              ? LOCK_RESOURCE_TYPE_RESPONSE
              : LOCK_RESOURCE_CLAUSE;
          return markRespondReadStatus({
            token,
            resource,
            id: item.id,
            isRead: true,
          });
        })
      );

      const hasSuccess = results.some(result => result.status === 'fulfilled');
      if (!hasSuccess) return;

      clearClarificationRespondCache({
        procurementId,
        procurementMitraId,
      });

      if (!cancelled && typeof onCompleteRef.current === 'function') {
        onCompleteRef.current();
      }
    };

    markUnread().catch(error => {
      console.error('Error marking clarification responds as read:', error);
    });

    return () => {
      cancelled = true;
    };
  }, [
    clauseDetailIdsKey,
    expectIsPertamina,
    procurementId,
    procurementMitraId,
    runKey,
  ]);
};

export default useMarkClarificationRespondsAsRead;
